import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { useStore } from "../../../contexts/store"
import { ConversationItem } from "../../../../../shared/src/types/Miscs"

import UserInfoAvatar from "../../Elements/UserInfoAvatar"
import UxIcon from "../../Elements/UxIcon"

import { Badge, BadgeProps, Box, Button, Divider, IconButton, styled } from "@mui/material"
import Link from "../../Elements/Link"


const ConversationsPreview = ({podId}: {podId: string}) => {
  const { t } = useTranslation()
  const { podStore, sessionStore, uiStore } = useStore()
  const deviceWidth = uiStore.deviceWidth
  const nofPreviews = 6
  if (!sessionStore.session) return null
  const userId = sessionStore.session.user.userId
  const rendered:{[id:string]:true} = {}

  const myConversations = podStore.getConversations(userId)
  if (myConversations) {
    // sort conversation list by time of last written message
    myConversations.sort((a: ConversationItem, b: ConversationItem) => {
      if (a.tLastMessage > b.tLastMessage) return -1
      if (a.tLastMessage < b.tLastMessage) return 1
      return 0
    })
    myConversations.forEach(c => rendered[c.threadId || ''] = true)
  }

  const allConversationsList = podStore.getConversations()?.filter(c => !rendered[c.threadId || ''])
  if (allConversationsList) allConversationsList.sort((a: ConversationItem, b: ConversationItem) => {
    if (a.tLastMessage > b.tLastMessage) return -1
    if (a.tLastMessage < b.tLastMessage) return 1
    return 0
  })

  if (!userId || myConversations === null) return null

  const myConversationsList = myConversations.slice(0,nofPreviews-1)

  const changeActiveConversation = (interactionId: string | null) => {
    uiStore.setActiveConversation(podId, interactionId)
    uiStore.setEditedMessageId(null)
  }

  const UserAvatar = ({item}: {item: ConversationItem}) => (
    <UserInfoAvatar key={item.lastMessageUserId} userId={item.lastMessageUserId} podId={podId} sx={{
      fontSize:"16px",
      height: "40px",
      width: "40px"
    }}/>
  )

  const previewConversation = (item:ConversationItem) => {
  return <Link key={item.threadId} noDecoration={true} to={`/pod/${podId}/conversations${item.interactionId ? `/${item.interactionId}` : ""}`} onClick={() => changeActiveConversation(item.interactionId)}>
    <Box sx={{
      alignItems: "center",
      display: "grid",
      gap: "12px",
      gridTemplateColumns: "max-content auto min-content",
      padding: "10px",
      borderRadius:1,
      '&:hover': {
        boxShadow:'0 0 10px 0 #0008',
      }
    }}>
      {item.hasUnreadMessages ?
        <UnreadBadge
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right'}}
          overlap="circular"
          variant="dot"
        >
          <UserAvatar item={item}/>
        </UnreadBadge>
      :
        <UserAvatar item={item}/>
      }
      <Box sx={{
        color: "textcolor.main",
        display: "grid",
        gridTemplateRows: "max-content max-content",
        paddingRight: "25px"
      }}>
        <Box sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          {item.lastMessageUserName}
        </Box>
        <Box sx={{
          fontWeight: 300,
          maxWidth: "500px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
         {item.lastMessage}
        </Box>
      </Box>
    </Box>
  </Link>
  }

  const otherConversations = (allConversationsList) ? allConversationsList.slice(0, nofPreviews - Math.max(1, myConversationsList.length)).map(item => previewConversation(item)) : null

  return (
    <Box sx={{
      display: "grid",
      gap: "5px",
      margin: "20px",
    }}>
      {myConversationsList.length === 0 ?
        <Box sx={{
          display: "grid",
          fontSize: "17px",
          fontStyle: "italic",
          fontWeight: 300,
          justifyContent: "center",
          padding: "8px",
          margin: "30px"
        }}>
          {t('No Conversations yet')}
        </Box>
        :
        <>
          { myConversationsList.map(item => previewConversation(item)) }
        </>
      }
      {(otherConversations && allConversationsList && (allConversationsList.length - myConversations.length > 0)) ? <Box>
        <Box sx={{textAlign:'center'}}>{t('otherConversations', {count: Math.max(0, allConversationsList.length - myConversations.length)})}</Box>
        { otherConversations }
      </Box> : null}
    </Box>
  )
}

export default observer(ConversationsPreview)

const UnreadBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    border: `2px solid #f8f8ff`,
    backgroundColor: theme.palette.secondary.main
  }
}))
